import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getHousekeeperById, createHousekeeper, updateHousekeeper, idcardOcr, idcardOcrTemp, uploadIdcardImage, uploadPhoto, uploadResume, uploadResumeTemp } from '../api/housekeeperApi';
import { uploadImageToCos } from '../api/cosApi';
import { parseIdCard } from '../utils/idCard';
import { ETHNICITY_OPTIONS, EDUCATION_OPTIONS, MARITAL_OPTIONS, ROLE_OPTIONS, CITY_OPTIONS, matchEthnicity } from '../constants/options';
import { parseServiceCity } from '../utils/jsonParser';
import './HousekeeperEditPage1.css';

const emptyFormData = {
  name: '', gender: '', age: null, phone: '', id_card: '',
  birthplace: '', birth_date: '', ethnicity: '汉族', education: '',
  marital_status: '', selectedCities: [], role: '', expected_salary: '',
  photo_url: '', zodiac_sign: '', chinese_zodiac: ''
};

export default function HousekeeperEditPage1({ id, mode, navigateTo }) {
  const [formData, setFormData] = useState({ ...emptyFormData });
  const [housekeeperId, setHousekeeperId] = useState(id || null);
  const [idcardFront, setIdcardFront] = useState('');
  const [idcardBack, setIdcardBack] = useState('');
  const [ocrProcessing, setOcrProcessing] = useState(false);
  const [parsedData, setParsedData] = useState(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [resumeFile, setResumeFile] = useState(null);
  const [parsingResume, setParsingResume] = useState(false);

  useEffect(() => {
    if (housekeeperId) {
      loadDetail(housekeeperId);
    }
  }, [housekeeperId]);

  const loadDetail = async (id) => {
    try {
      const res = await getHousekeeperById(id);
      const h = res.data;
      if (h && h.id) {
        const selectedCities = parseServiceCity(h.service_city);
        setFormData({
          name: h.name || '', gender: h.gender || '', age: h.age, phone: h.phone || '',
          id_card: h.id_card || '', birthplace: h.birthplace || '', birth_date: h.birth_date || '',
          ethnicity: h.ethnicity || '汉族', education: h.education || '',
          marital_status: h.marital_status || '', selectedCities,
          role: h.role || '', expected_salary: h.expected_salary ? String(h.expected_salary) : '',
          photo_url: h.photo_url || '', zodiac_sign: h.zodiac_sign || '',
          chinese_zodiac: h.chinese_zodiac || ''
        });
        let front = '', back = '';
        if (h.id_card_url) {
          try { const urls = JSON.parse(h.id_card_url); if (Array.isArray(urls) && urls.length >= 2) { front = urls[0] || ''; back = urls[1] || ''; } } catch (e) {}
        }
        setIdcardFront(front);
        setIdcardBack(back);
      }
    } catch (e) { console.error('获取阿姨详情失败:', e); }
  };

  const onInput = (e) => {
    const name = e.target.dataset.name || e.target.name;
    let value = e.target.value;
    if (name === 'id_card' && value.length === 18) {
      const info = parseIdCard(value);
      if (info) {
        setFormData(prev => ({ ...prev, id_card: value, gender: info.gender, birth_date: info.birthDate, age: info.age, zodiac_sign: info.zodiacSign, chinese_zodiac: info.chineseZodiac }));
        return;
      }
    }
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const getMissingRequiredFields = () => {
    const missing = [];
    if (!formData.name) missing.push('姓名');
    if (!formData.phone) missing.push('手机号');
    if (!formData.id_card) missing.push('身份证号');
    if (!formData.role) missing.push('角色');
    if (!formData.photo_url) missing.push('头像');
    return missing;
  };

  const doSave = async (showToast) => {
    const missing = getMissingRequiredFields();
    if (missing.length > 0) {
      alert('请先填写/上传' + missing.join('、'));
      return null;
    }
    setSubmitting(true);
    try {
      const submitData = { ...formData };
      if (submitData.expected_salary === '' || submitData.expected_salary === null) submitData.expected_salary = null;
      else submitData.expected_salary = parseInt(submitData.expected_salary);
      if (submitData.age === '' || submitData.age === null) submitData.age = null;
      if (submitData.photo_url === '') submitData.photo_url = null;

      // 处理多选城市
      submitData.service_city = JSON.stringify(submitData.selectedCities || []);
      delete submitData.selectedCities;

      // 将暂存的身份证图片URL打包进保存数据
      if (idcardFront || idcardBack) {
        submitData.id_card_url = JSON.stringify([idcardFront || '', idcardBack || '']);
      }

      if (housekeeperId) {
        await updateHousekeeper(housekeeperId, submitData);
        if (showToast) alert('保存成功');
        return { id: housekeeperId };
      } else {
        submitData.resume_status = 'draft';
        const res = await createHousekeeper(submitData);
        if (res.data && res.data.id) {
          setHousekeeperId(res.data.id);
          // 创建成功后，如果有暂存的身份证正面，补调用OCR
          if (idcardFront) {
            try {
              const ocrRes = await idcardOcr(res.data.id, idcardFront);
              if (ocrRes.data?.success && ocrRes.data.extracted_data) {
                const data = ocrRes.data.extracted_data;
                setParsedData(data);
                setFormData(prev => ({
                  ...prev,
                  name: data.name || prev.name,
                  id_card: data.id_card || prev.id_card,
                  birthplace: data.birthplace || prev.birthplace,
                  ethnicity: matchEthnicity(data.ethnicity) || prev.ethnicity,
                }));
                if (data.id_card && data.id_card.length === 18) {
                  const info = parseIdCard(data.id_card);
                  if (info) setFormData(prev => ({ ...prev, gender: info.gender, birth_date: info.birthDate, age: info.age, zodiac_sign: info.zodiacSign, chinese_zodiac: info.chineseZodiac }));
                }
              }
            } catch (ocrErr) { console.error('OCR失败:', ocrErr); }
          }
          if (showToast) alert('保存成功');
          return res.data;
        }
      }
    } catch (e) {
      // 409错误（身份证重复）总是显示，不管showToast是什么值
      if (e.response?.status === 409) {
        alert(e.response.data.detail || '当前身份证号码与已有的号码相同，请更换后录入');
      } else {
        alert('保存失败：' + (e.response?.data?.detail || '网络错误'));
      }
      return null;
    } finally { setSubmitting(false); }
  };

  // 检查该阿姨是否已有背调记录
  const hasBackgroundCheck = async (hid) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`/api/housekeepers/${hid}/background-check/latest`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return res.data.code === 200 && !!res.data.data;
    } catch (error) {
      console.error('检查背调记录失败', error);
      return false;
    }
  };

  const save = async () => {
    const result = await doSave(false);
    if (!result) return; // 校验未通过或保存失败（doSave 内已提示）
    const hid = result.id || housekeeperId;
    if (!hid) return;
    // 无背调信息则直接跳到背调查询页；有背调信息则留在本页并提示保存成功
    const hasBg = await hasBackgroundCheck(hid);
    if (hasBg) {
      alert('保存成功');
    } else {
      navigateTo('background-check', { id: hid });
    }
  };

  const goToBackgroundCheck = async () => {
    if (!housekeeperId) {
      alert('请先保存阿姨信息');
      return;
    }
    try {
      // 先检查是否有背调记录
      const token = localStorage.getItem('token');
      const res = await axios.get(`/api/housekeepers/${housekeeperId}/background-check/latest`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.data.code === 200 && res.data.data) {
        // 有记录，跳转到结果页
        navigateTo('background-check-result', { id: housekeeperId, checkId: res.data.data.check_id });
      } else {
        // 无记录，跳转到查询页
        navigateTo('background-check', { id: housekeeperId });
      }
    } catch (error) {
      console.error('检查背调记录失败', error);
      // 失败时默认跳转到查询页
      navigateTo('background-check', { id: housekeeperId });
    }
  };

  const nextPage = async () => {
    // 角色必填检查
    if (!formData.role) {
      alert('请选择阿姨角色');
      return;
    }
    const missing = getMissingRequiredFields();
    if (missing.length > 0) {
      alert('请先填写/上传' + missing.join('、'));
      return;
    }
    setSubmitting(true);
    try {
      const submitData = { ...formData };
      if (submitData.expected_salary === '' || submitData.expected_salary === null) submitData.expected_salary = null;
      else submitData.expected_salary = parseInt(submitData.expected_salary);
      if (submitData.age === '' || submitData.age === null) submitData.age = null;
      if (submitData.photo_url === '') submitData.photo_url = null;

      // 处理多选城市
      submitData.service_city = JSON.stringify(submitData.selectedCities || []);
      delete submitData.selectedCities;

      // 将暂存的身份证图片URL打包进保存数据
      if (idcardFront || idcardBack) {
        submitData.id_card_url = JSON.stringify([idcardFront || '', idcardBack || '']);
      }

      if (housekeeperId) {
        await updateHousekeeper(housekeeperId, submitData);
        // 无背调信息则直接跳到背调查询页，否则进入下一页
        const hasBg = await hasBackgroundCheck(housekeeperId);
        navigateTo(hasBg ? 'edit2' : 'background-check', { id: housekeeperId });
      } else {
        submitData.resume_status = 'draft';
        const res = await createHousekeeper(submitData);
        if (res.data && res.data.id) {
          setHousekeeperId(res.data.id);
          // 创建成功后，如果有暂存的身份证正面，补调用OCR
          if (idcardFront) {
            try {
              const ocrRes = await idcardOcr(res.data.id, idcardFront);
              if (ocrRes.data?.success && ocrRes.data.extracted_data) {
                const data = ocrRes.data.extracted_data;
                setParsedData(data);
                setFormData(prev => ({
                  ...prev,
                  name: data.name || prev.name,
                  id_card: data.id_card || prev.id_card,
                  birthplace: data.birthplace || prev.birthplace,
                  ethnicity: matchEthnicity(data.ethnicity) || prev.ethnicity,
                }));
                if (data.id_card && data.id_card.length === 18) {
                  const info = parseIdCard(data.id_card);
                  if (info) setFormData(prev => ({ ...prev, gender: info.gender, birth_date: info.birthDate, age: info.age, zodiac_sign: info.zodiacSign, chinese_zodiac: info.chineseZodiac }));
                }
              }
            } catch (ocrErr) { console.error('OCR失败:', ocrErr); }
          }
          // 无背调信息则直接跳到背调查询页，否则进入下一页
          const hasBg = await hasBackgroundCheck(res.data.id);
          navigateTo(hasBg ? 'edit2' : 'background-check', { id: res.data.id });
        }
      }
    } catch (e) {
      // 409错误（身份证重复）总是显示
      if (e.response?.status === 409) {
        alert(e.response.data.detail || '当前身份证号码与已有的号码相同，请更换后录入');
      } else {
        alert('保存失败：' + (e.response?.data?.detail || '网络错误'));
      }
    } finally { setSubmitting(false); }
  };

  const uploadIdcardFront = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setOcrProcessing(true);
    try {
      const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
      const accessUrl = await uploadImageToCos(file, 'housekeeper-idcard', userInfo.user_id);
      setIdcardFront(accessUrl);
      // 触发OCR识别
      const doOcr = async (ocrRes) => {
        if (ocrRes.data?.success && ocrRes.data.extracted_data) {
          const data = ocrRes.data.extracted_data;
          setParsedData(data);
          setFormData(prev => ({
            ...prev,
            name: data.name || prev.name,
            id_card: data.id_card || prev.id_card,
            birthplace: data.birthplace || prev.birthplace,
            ethnicity: matchEthnicity(data.ethnicity) || prev.ethnicity,
          }));
          if (data.id_card && data.id_card.length === 18) {
            const info = parseIdCard(data.id_card);
            if (info) setFormData(prev => ({ ...prev, gender: info.gender, birth_date: info.birthDate, age: info.age, zodiac_sign: info.zodiacSign, chinese_zodiac: info.chineseZodiac }));
          }
          alert('身份证识别成功');
        }
      };

      if (housekeeperId) {
        await uploadIdcardImage(housekeeperId, accessUrl, 'front');
        try {
          const ocrRes = await idcardOcr(housekeeperId, accessUrl);
          await doOcr(ocrRes);
        } catch (ocrErr) { console.error('OCR失败:', ocrErr); }
      } else {
        // 新建阿姨时，使用临时OCR端点立即识别
        try {
          const ocrRes = await idcardOcrTemp(accessUrl);
          await doOcr(ocrRes);
        } catch (ocrErr) { console.error('临时OCR失败:', ocrErr); }
        alert('图片已暂存，保存时将一并上传');
      }
    } catch (e) { alert('上传失败'); }
    finally { setOcrProcessing(false); e.target.value = ''; }
  };

  const uploadIdcardBack = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
      const accessUrl = await uploadImageToCos(file, 'housekeeper-idcard', userInfo.user_id);
      setIdcardBack(accessUrl);
      if (housekeeperId) {
        await uploadIdcardImage(housekeeperId, accessUrl, 'back');
        alert('上传成功');
      } else {
        alert('图片已暂存，保存时将一并上传');
      }
    } catch (e) { alert('上传失败'); }
    finally { e.target.value = ''; }
  };

  const uploadAvatar = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingAvatar(true);
    try {
      const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
      const accessUrl = await uploadImageToCos(file, 'head-image', userInfo.user_id);
      setFormData(prev => ({ ...prev, photo_url: accessUrl }));
      if (housekeeperId) {
        await uploadPhoto(housekeeperId, accessUrl);
        alert('头像上传成功');
      } else {
        alert('头像已暂存，保存时将一并上传');
      }
    } catch (e) { alert('上传头像失败'); }
    finally { setUploadingAvatar(false); e.target.value = ''; }
  };

  const handleResumeUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setResumeFile(file);
    setParsingResume(true);
    const fd = new FormData();
    fd.append('resume', file);
    // 添加 user_id 和 original_filename 以生成规范文件名
    const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
    if (userInfo.user_id) {
      fd.append('user_id', userInfo.user_id);
    }
    fd.append('original_filename', file.name);
    try {
      const url = housekeeperId ? null : null;
      const res = housekeeperId
        ? await uploadResume(housekeeperId, fd)
        : await uploadResumeTemp(fd);
      if (res.data?.extracted_data) {
        const data = res.data.extracted_data;
        setParsedData(data);
        setFormData(prev => ({
          ...prev,
          name: data.name || prev.name,
          phone: data.phone || prev.phone,
          id_card: data.id_card || prev.id_card,
          birthplace: data.birthplace || prev.birthplace,
        }));
        if (data.id_card && data.id_card.length === 18) {
          const info = parseIdCard(data.id_card);
          if (info) setFormData(prev => ({ ...prev, gender: info.gender, birth_date: info.birthDate, age: info.age }));
        }
        alert('简历解析成功，已自动填充信息');
      }
    } catch (e) { alert('简历解析失败'); }
    finally { setParsingResume(false); e.target.value = ''; }
  };

  const title = housekeeperId && mode === 'continue' ? '继续录入信息' : '新增阿姨';

  return (
    <div className="edit1-container">
      <div className="edit-header">
        <button className="edit-back" onClick={() => navigateTo('home')}>←</button>
        <span className="edit-title">{title}</span>
        <button className="edit-close" onClick={() => navigateTo('home')}>✕</button>
      </div>

      <div className="edit-body">
        <div className="form-section">
          <div className="section-title">上传简历（可选）</div>
          <div className="resume-upload">
            <label className="btn-select-resume">
              选择简历文件
              <input type="file" accept=".pdf,.doc,.docx,.txt,.md,.jpg,.png" onChange={handleResumeUpload} style={{ display: 'none' }} />
            </label>
            {parsingResume && <div className="parsing-hint">正在 AI 解析中...</div>}
            {resumeFile && !parsingResume && <div className="file-hint">已选择：{resumeFile.name}</div>}
          </div>
        </div>

        <div className="form-section idcard-section">
          <div className="section-title">上传身份证{mode === 'edit' ? '（不可修改）' : ''}</div>
          <div className="idcard-grid">
            <div className="idcard-item">
              {idcardFront ? (
                <>
                  <img className="idcard-image" src={idcardFront} alt="人像面" />
                  {mode !== 'edit' && <label className="idcard-reupload"><input type="file" accept="image/*" onChange={uploadIdcardFront} style={{ display: 'none' }} />重新上传</label>}
                </>
              ) : (
                mode !== 'edit' && (
                  <label className="idcard-placeholder">
                    <span className="placeholder-plus">+</span>
                    <span className="placeholder-text">上传人像面</span>
                    <input type="file" accept="image/*" onChange={uploadIdcardFront} style={{ display: 'none' }} />
                  </label>
                )
              )}
            </div>
            <div className="idcard-item">
              {idcardBack ? (
                <>
                  <img className="idcard-image" src={idcardBack} alt="国徽面" />
                  {mode !== 'edit' && <label className="idcard-reupload"><input type="file" accept="image/*" onChange={uploadIdcardBack} style={{ display: 'none' }} />重新上传</label>}
                </>
              ) : (
                mode !== 'edit' && (
                  <label className="idcard-placeholder">
                    <span className="placeholder-plus">+</span>
                    <span className="placeholder-text">上传国徽面</span>
                    <input type="file" accept="image/*" onChange={uploadIdcardBack} style={{ display: 'none' }} />
                  </label>
                )
              )}
            </div>
          </div>
          {ocrProcessing && <div className="ocr-hint">正在 AI 识别身份证...</div>}
          {parsedData && (
            <div className="parsed-hint">
              <div className="hint-title">已自动填充以下信息：</div>
              <div className="hint-tags">
                {parsedData.name && <span className="parsed-tag">姓名：{parsedData.name}</span>}
                {parsedData.id_card && <span className="parsed-tag">身份证：{parsedData.id_card}</span>}
                {parsedData.gender && <span className="parsed-tag">性别：{parsedData.gender}</span>}
                {parsedData.birth_date && <span className="parsed-tag">出生：{parsedData.birth_date}</span>}
                {parsedData.ethnicity && <span className="parsed-tag">民族：{parsedData.ethnicity}</span>}
                {parsedData.birthplace && <span className="parsed-tag">籍贯：{parsedData.birthplace}</span>}
              </div>
            </div>
          )}
        </div>

        <div className="form-section avatar-section">
          <div className="section-title">上传头像（必填）</div>
          <label className="avatar-upload-btn">
            {formData.photo_url ? <img className="avatar-preview" src={formData.photo_url} alt="头像" /> : <span className="avatar-placeholder-inner">👤</span>}
            <span className="avatar-camera">📷</span>
            <input type="file" accept="image/*" multiple onChange={uploadAvatar} style={{ display: 'none' }} />
          </label>
          {uploadingAvatar && <div className="uploading-hint">上传中...</div>}
        </div>

        <div className="form-section">
          <div className="section-title">基本信息（必填）</div>
          <div className="form-group">
            <label className="form-label required">姓名</label>
            <input className="form-input" name="name" value={formData.name} onChange={onInput} placeholder="请输入姓名" />
          </div>
          <div className="form-group">
            <label className="form-label required">联系电话</label>
            <input className="form-input" name="phone" type="tel" maxLength={11} value={formData.phone} onChange={onInput} placeholder="请输入手机号" />
          </div>
          <div className="form-group">
            <label className="form-label required">身份证号码</label>
            <input className="form-input" name="id_card" maxLength={18} value={formData.id_card} onChange={onInput} placeholder="请输入身份证号码" />
          </div>
          <div className="form-group">
            <label className="form-label required">角色</label>
            <select className="form-picker" value={formData.role} onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value }))}>
              <option value="">请选择</option>
              {ROLE_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
            </select>
          </div>
        </div>

        <div className="form-section">
          <div className="section-title">基本信息（选填）</div>
          <div className="form-group">
            <label className="form-label">性别</label>
            <input className="form-input readonly" value={formData.gender} readOnly placeholder="根据身份证自动生成" />
          </div>
          <div className="form-group">
            <label className="form-label">出生年月</label>
            <input className="form-input readonly" value={formData.birth_date} readOnly placeholder="根据身份证自动生成" />
          </div>
          <div className="form-group">
            <label className="form-label">年龄</label>
            <input className="form-input readonly" value={formData.age ? `${formData.age}岁` : ''} readOnly placeholder="根据身份证自动生成" />
          </div>
          <div className="form-group">
            <label className="form-label">民族</label>
            <select className="form-picker" value={formData.ethnicity} onChange={(e) => setFormData(prev => ({ ...prev, ethnicity: e.target.value }))}>
              <option value="">请选择</option>
              {ETHNICITY_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">籍贯</label>
            <input className="form-input" data-name="birthplace" value={formData.birthplace} onChange={onInput} placeholder="请输入籍贯" />
          </div>
          <div className="form-group">
            <label className="form-label">最高学历</label>
            <select className="form-picker" value={formData.education} onChange={(e) => setFormData(prev => ({ ...prev, education: e.target.value }))}>
              <option value="">请选择</option>
              {EDUCATION_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">婚姻状况</label>
            <select className="form-picker" value={formData.marital_status} onChange={(e) => setFormData(prev => ({ ...prev, marital_status: e.target.value }))}>
              <option value="">请选择</option>
              {MARITAL_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">接单城市</label>
            <div className="city-multi-select">
              <div className="city-selected-display" onClick={() => setFormData(prev => ({ ...prev, showCityDropdown: !prev.showCityDropdown }))}>
                {formData.selectedCities.length > 0 ? formData.selectedCities.join('、') : '请选择城市'}
                <span className="dropdown-arrow">▼</span>
              </div>
              {formData.showCityDropdown && (
                <div className="city-dropdown">
                  {CITY_OPTIONS.map(city => (
                    <label key={city} className="city-option">
                      <input
                        type="checkbox"
                        checked={formData.selectedCities.includes(city)}
                        onChange={(e) => {
                          const cities = e.target.checked
                            ? [...formData.selectedCities, city]
                            : formData.selectedCities.filter(c => c !== city);
                          setFormData(prev => ({ ...prev, selectedCities: cities }));
                        }}
                      />
                      <span>{city}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">期望薪资</label>
            <input className="form-input" data-name="expected_salary" type="number" value={formData.expected_salary} onChange={onInput} placeholder="请输入期望薪资（元/月）" />
          </div>
        </div>
      </div>

      <div className="action-bar">
        <button className="action-btn" onClick={save} disabled={submitting}>{submitting ? '保存中...' : '保存'}</button>
        {housekeeperId && (
          <button className="action-btn" onClick={goToBackgroundCheck}>信用查询</button>
        )}
        <button className="action-btn primary" onClick={nextPage} disabled={submitting}>下一页</button>
      </div>
    </div>
  );
}